/**
 * @file React context for the in-app agent: attachments, model params, agentic
 * progress, panel geometry, and the imperative handles (`streamModel`,
 * `cancelStream`, `clearMessages`, etc.) provided by `AIProvider`. Consumed by
 * `Agent`, `MessagesList`, `ModelInput`, and any UI piece that drives the active
 * conversation. Nothing transcript-derived lives here — not the messages, not
 * their count: `AITranscriptContext` owns both, so this value never changes when
 * a message lands. Types `ChatMessage` and `*FileAttachment` are also re-exported
 * from here. A `ChatMessage` that answered through `runNamedAnalyticsQuery` carries
 * `analyticsQueries`, the row-free provenance of each call in that run, in call
 * order; the key is absent, never `undefined`, when the run read nothing. No panel geometry lives here: the agent panel's width, dock side and
 * open flag are `uiState.agentSidebar`, read directly by `Agent` and
 * `MappingReviewPanel`. They were once published here so two siblings could share
 * a width, which meant a hover on the drag handle minted a new context value for
 * every consumer.
 */
import { createContext, type Dispatch, type SetStateAction } from 'react';
import { LanguageModelTaskResult, AgentModelParams, AgenticFinishEvent, AgentToolName, AnalyticsQueryProvenance } from '#types';

interface ChatMessage {
  id: string;
  sender: string;
  content: string | null;
  timestamp: string | null;
  agenticFinish?: AgenticFinishEvent;
  /** What each analytics query of the run behind this answer read, in call order. */
  analyticsQueries?: AnalyticsQueryProvenance[];
  attachments?: FileAttachment[];
}

export interface AgenticProgress {
  iteration: number;
  maxIterations: number;
  toolCalls: number;
  isRunning: boolean;
  toolLabels: string[];
}

export interface TabularFileAttachment {
  kind: 'tabular';
  filename: string;
  fileId: string;
  fileFolder: string;
  originalPath: string;
  contentType: string;
  sizeBytes: number;
}

export interface EmailFileAttachment {
  kind: 'email';
  filename: string;
  fileId: string;
  fileFolder: string;
  originalPath: string;
  contentType: string;
  sizeBytes: number;
  subject: string;
  from?: string;
  body: string;
}

export interface ImageFileAttachment {
  kind: 'image';
  filename: string;
  fileId: string;
  fileFolder: string;
  originalPath: string;
  contentType: string;
  sizeBytes: number;
}

export type FileAttachment = TabularFileAttachment | EmailFileAttachment | ImageFileAttachment;

export interface AIContextType {
  addMessage: (message: ChatMessage) => void;
  clearMessages: () => void;
  callModel: (inputValue: string) => Promise<void>;
  streamModel: (inputValue: string, tools?: AgentToolName[]) => Promise<void>;
  streamModelAgentic: (inputValue: string, objective: string, tools?: AgentToolName[]) => Promise<void>;
  /** Import entry: upload the given files and run the agent to import them into a new form of `formType`. */
  startDocumentImportRun: (files: File[], formType: string) => Promise<void>;
  /** Import entry for an open form: upload the given files and run the agent to import them into the bound form version (no draft form is created). */
  importIntoForm: (files: File[], binding: { formId: string; formVersionId: string }) => Promise<void>;
  /** Re-run the active import against a new form type (cancels the current run, archives its draft). */
  changeImportFormType: (formType: string) => Promise<void>;
  /** Archive the active import run's unapplied draft form and clear it. */
  discardImportRun: () => Promise<void>;
  cancelStream: () => void;
  activeMessageContent: string | null;
  setActiveMessageContent: Dispatch<SetStateAction<string | null>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  paramsLoading: boolean;
  lastTaskResult?: LanguageModelTaskResult;
  modelParams: AgentModelParams;
  setModelParams: Dispatch<SetStateAction<AgentModelParams>>;
  agenticProgress: AgenticProgress | null;
  attachFiles: (files: File[], options?: { sanitizeEmail?: boolean }) => Promise<void>;
  signifyAuthorized: boolean;
  setSignifyAuthorized: Dispatch<SetStateAction<boolean>>;
  mcpAuthorized: boolean;
  setMcpAuthorized: Dispatch<SetStateAction<boolean>>;
}

export const AIContext = createContext<AIContextType>({
  addMessage: () => {},
  clearMessages: () => {},
  callModel: async () => {},
  streamModel: async () => {},
  streamModelAgentic: async () => {},
  startDocumentImportRun: async () => {},
  importIntoForm: async () => {},
  changeImportFormType: async () => {},
  discardImportRun: async () => {},
  cancelStream: () => {},
  activeMessageContent: null,
  setActiveMessageContent: () => {},
  isLoading: false,
  setIsLoading: () => {},
  paramsLoading: false,
  modelParams: {
    system: '',
    model: '',
    maxTokens: 0,
    temperature: 0,
    topP: 0,
    thinkingMode: 'thinking',
    reasoningEffort: 'medium',
  },
  setModelParams: () => {},
  agenticProgress: null,
  attachFiles: async () => {},
  signifyAuthorized: false,
  setSignifyAuthorized: () => {},
  mcpAuthorized: false,
  setMcpAuthorized: () => {},
});

export { type ChatMessage };


